'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "disabled", "duration", "failed", "loading", "onClick", "onLoadingFinish", "preventClicksOnLoading", "keepResultAfterFinish", "resultIcon", "size", "spinnerProps", "truncateTooltip", "use"];
import { Component } from 'react';
import classNames from 'classnames';
import emptyFunction from 'react-utils/emptyFunction';
import UIButton, { renderContents } from './UIButton';
import UILoadingSpinner from '../loading/UILoadingSpinner';
import { callIfPossible } from '../core/Functions';
import { USE_CLASSES } from '../button/ButtonConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class UILoadingButton extends Component {
  static getDerivedStateFromProps(props, state) {
    const {
      prevLoading
    } = state;
    const {
      failed,
      loading,
      resultIcon
    } = props;
    const hasResult = resultIcon != null && !failed;
    const shouldDisplayResult = hasResult && prevLoading && !loading;
    if (loading !== prevLoading) {
      return {
        isLoading: loading,
        prevLoading: loading,
        displayResult: shouldDisplayResult
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.componentDidUpdate = (prevProps, prevState) => {
      const {
        duration
      } = this.props;
      const {
        isLoading: wasLoading
      } = prevState;
      const {
        isLoading
      } = this.state;
      const startedLoading = !wasLoading && isLoading;
      if (duration && startedLoading) {
        clearTimeout(this.loadingTimeout);
        this.loadingTimeout = setTimeout(this.handleLoadingFinish, duration);
      }
    };
    this.handleLoadingFinish = () => {
      const {
        failed,
        resultIcon
      } = this.props;
      const hasResult = resultIcon != null && !failed;
      this.setState({
        isLoading: false,
        displayResult: hasResult
      });
      if (!hasResult) {
        this.setDisplayResult();
      }
    };
    this.setDisplayResult = () => {
      const {
        onLoadingFinish,
        keepResultAfterFinish
      } = this.props;
      this.setState({
        displayResult: keepResultAfterFinish
      });
      onLoadingFinish();
    };
    this.handleClick = evt => {
      const {
        duration,
        onClick,
        preventClicksOnLoading
      } = this.props;
      const {
        displayResult,
        isLoading
      } = this.state;
      if (preventClicksOnLoading && (isLoading || displayResult)) {
        evt.preventDefault();
        return;
      }
      callIfPossible(onClick, evt);
      if (evt.defaultPrevented) {
        return;
      }
      if (!isLoading && duration) {
        this.setState({
          isLoading: true
        });
      }
    };
    const {
      loading
    } = props;
    this.state = {
      displayResult: false,
      isLoading: loading || false,
      prevLoading: loading
    };
  }
  componentDidMount() {
    const {
      duration
    } = this.props;
    const {
      isLoading
    } = this.state;
    if (duration && isLoading) {
      this.loadingTimeout = setTimeout(this.handleLoadingFinish, duration);
    }
  }
  componentWillUnmount() {
    clearTimeout(this.loadingTimeout);
  }
  render() {
    const _this$props = this.props,
      {
        children,
        className,
        disabled,
        failed,
        resultIcon,
        size,
        spinnerProps,
        truncateTooltip,
        use
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      isLoading,
      displayResult
    } = this.state;
    const disabledUnlessLoading = disabled && !isLoading && !displayResult;
    let loadingState = 'idle';
    if (isLoading) {
      loadingState = 'loading';
    }
    if (displayResult) {
      loadingState = 'success';
    }

    // this wraps any bare strings within a <span> to provide a more predictable DOM for applying automatic spacing rules.
    const renderedContents = renderContents(children);
    return /*#__PURE__*/_jsxs(UIButton, Object.assign({}, rest, {
      size: size,
      use: use,
      "data-loading": isLoading,
      "data-loading-state": loadingState,
      disabled: disabledUnlessLoading,
      className: classNames('private-loading-button', className, USE_CLASSES[use], (isLoading || displayResult) && 'loading private-loading-button--loading'),
      onClick: this.handleClick,
      truncateTooltip: truncateTooltip || children,
      children: [/*#__PURE__*/_jsx("span", {
        className: "private-loading-button__content private-button--internal-spacing",
        children: renderedContents
      }), /*#__PURE__*/_jsx(UILoadingSpinner, Object.assign({}, spinnerProps, {
        className: "private-loading-button__spinner",
        "data-loading": null,
        failed: failed,
        use: use,
        size: size,
        resultIcon: resultIcon,
        showResult: displayResult,
        onResultDisplayFinish: this.setDisplayResult
        // used to trigger an addition to the a11y tree and read out the initial loading state #8675
        ,
        hidden: !(isLoading || displayResult)
      }))]
    }));
  }
}
UILoadingButton.displayName = 'UILoadingButton';
UILoadingButton.defaultProps = {
  _useNativeButton: true,
  disabled: false,
  external: false,
  responsive: true,
  truncate: false,
  type: 'button',
  failed: false,
  onLoadingFinish: emptyFunction,
  preventClicksOnLoading: true,
  keepResultAfterFinish: false,
  resultIcon: 'success',
  size: 'medium',
  use: 'secondary'
};